/* eslint-disable @typescript-eslint/naming-convention */
import Alpine from 'alpinejs'

interface CookieFirstConsent {
	necessary: boolean
	functional: boolean
	performance: boolean
	advertising: boolean
}

interface Cookie {
	name: string
	value: string
	expires: number
	path: string
}

interface gpdrData {
	cookies: Cookie[]
	tabIndex: number
	isLoading: boolean
	shouldShow: boolean
	updateConsent(consents: CookieFirstConsent, defaultConsent?: boolean): void
	loadCookies(): void
	submitConsent(e: Event): void
	acceptAllConsent(): void
	necessaryOnly(): void
	init(): void
}

export default {
	name: 'gpdr',
	component(props: { preconsent: any; cookiesUrl: string }): Alpine.AlpineComponent<gpdrData> {
		const { preconsent, cookiesUrl } = props
		// @ts-expect-error $el is provided by Alpine.js
		const form = this.$el.querySelector('form')
		const inputs = form.querySelectorAll('input[type="checkbox"]')

		return {
			cookies: [],
			tabIndex: 0,
			isLoading: false,
			shouldShow: false,

			updateConsent(consents: CookieFirstConsent, defaultConsent = false) {
				const shopifyConsent = {
					preferences: consents.functional,
					analytics: consents.performance,
					marketing: consents.advertising,
					sale_of_data: consents.advertising,
					metafields: [{ key: 'user_interaction', value: 'true' }]
				}

				if (window.Shopify?.customerPrivacy && !defaultConsent) {
					window.Shopify.customerPrivacy.setTrackingConsent(shopifyConsent)
				}

				// Dispatch dataLayer event
				if (window.dataLayer && window.gtag) {
					const gtmConsent = {
						security_storage: 'granted',
						ad_storage: consents.advertising ? 'granted' : 'denied',
						ad_user_data: consents.advertising ? 'granted' : 'denied',
						ad_personalization: consents.advertising ? 'granted' : 'denied',
						analytics_storage: consents.performance ? 'granted' : 'denied',
						functionality_storage: consents.functional ? 'granted' : 'denied',
						personalization_storage: consents.functional ? 'granted' : 'denied'
					}
					window.gtag('consent', defaultConsent ? 'default' : 'update', gtmConsent)
				} else {
					console.warn('window.dataLayer or window.gtag is not defined')
				}

				// Close slideout
				this.$store.gpdr.isSlideoutOpened = false

				// Close GPDR banner
				this.shouldShow = false

				return consents
			},

			async loadCookies() {
				if (this.cookies.length === 0) {
					this.isLoading = true
					try {
						const response = await fetch(cookiesUrl)
						if (!response.ok) {
							throw new Error('Failed to fetch cookies data')
						}
						this.cookies = (await response.json()).cookies
					} catch (error) {
						console.error('Error loading cookies:', error)
						this.cookies = []
					} finally {
						this.isLoading = false
					}
				}
			},

			submitConsent(e: Event) {
				const form = (e.target as HTMLElement).closest('form')
				if (!form) return

				const inputs = form.querySelectorAll('input[type="checkbox"]')
				const consent = {
					necessary: true,
					functional: false,
					performance: false,
					advertising: false
				}
				inputs.forEach(cb => {
					consent[(cb as HTMLInputElement).name] = (cb as HTMLInputElement).checked
				})
				this.updateConsent(consent)
			},

			necessaryOnly() {
				this.updateConsent({
					necessary: true,
					functional: false,
					performance: false,
					advertising: false
				})

				inputs.forEach(cb => {
					;(cb as HTMLInputElement).checked = true
				})
			},

			acceptAllConsent() {
				this.updateConsent({
					necessary: true,
					functional: true,
					performance: true,
					advertising: true
				})

				inputs.forEach(cb => {
					;(cb as HTMLInputElement).checked = true
				})
			},

			init() {
				// Initialize the global store
				if (!this.$store.gpdr) {
					this.$store.gpdr = {
						isSlideoutOpened: false,
						shouldShowBanner: false
					}
				}

				// Load shopify consent api
				window.Shopify.loadFeatures(
					[
						{
							name: 'consent-tracking-api',
							version: '0.1'
						}
					],
					error => {
						if (!error) {
							// Restore shopify consents
							const rawConsents = window.Shopify.customerPrivacy?.currentVisitorConsent() || {}
							const consents = {
								necessary: true,
								performance: rawConsents.analytics === '' && preconsent?.performance ? preconsent.performance : rawConsents.analytics === 'yes',
								functional: rawConsents.preferences === '' && preconsent?.functional ? preconsent.functional : rawConsents.preferences === 'yes',
								advertising: rawConsents.marketing === '' && preconsent?.advertising ? preconsent.advertising : rawConsents.marketing === 'yes'
							}
							this.updateConsent(consents, true)

							inputs.forEach(cb => {
								;(cb as HTMLInputElement).checked = consents[(cb as HTMLInputElement).name]
							})

							const hasEmptyConsent = Object.values(rawConsents).some(value => value === '')
							const shouldShowBanner = window.Shopify?.customerPrivacy?.shouldShowBanner() || false
							// eslint-disable-next-line @shopify/binary-assignment-parens
							const userInteraction = window.Shopify?.customerPrivacy?.getTrackingConsentMetafield('user_interaction') === 'true'
							this.shouldShow = shouldShowBanner && (hasEmptyConsent || !userInteraction)
							// Make sure to show / hide footer CTA based on eligibility
							this.$store.gpdr.shouldShowBanner = shouldShowBanner
						}
					}
				)

				// Watch slideout tabIndex
				this.$watch('tabIndex', (value: number) => {
					if (value === 1) {
						this.loadCookies()
					}
				})
			}
		}
	}
}
